/* Per-(source,event) presentation: turns a notification into an icon + message + in-app route */
import {
  ACCESS_MANAGEMENT_SOURCE,
  AccessRequestNotification,
  parseAccessRequestNotification,
  ResolutionAction,
  XptNotification
} from './notificationModels';

/** Semantic weight, kept separate from iconName so a UI layer can pick its own iconography. */
export type NotificationSeverity = 'success' | 'critical' | 'pending' | 'unknown';

export interface NotificationPresentation {
  iconName: string; // Cloudscape icon name
  severity: NotificationSeverity;
  /** Short scannable summary, e.g. "Access request approved". Sentence case, no trailing period. */
  title: string;
  /** Full detail line, e.g. who requested what for which business group. */
  message: string;
  /** In-app route the row's action goes to. Null when the item is not click-through. */
  route: string | null;
}

/** `owns` decides whether this presenter handles the notification; `present` renders it. */
interface NotificationPresenter {
  owns: (notification: XptNotification) => boolean;
  present: (notification: XptNotification) => NotificationPresentation;
}

/** Fallback for an unowned or unparseable notification: shown, but marked as failed to load. */
const genericPresentation = (): NotificationPresentation => ({
  iconName: 'status-warning',
  severity: 'unknown',
  title: 'Notification unavailable',
  message: 'This notification could not be loaded',
  route: null
});

// ── Access-management presenter ──────────────────────────────────────────────────────────────────

/** Global, cross-business-group access management console — the reviewer's landing page. */
const GLOBAL_ACCESS_MANAGEMENT_PATH = '/admin-console/access-management';

const nameAlias = (name: string, alias: string): string => `${name} (${alias})`;

/** The outcome, or null while pending. From the item's own kind, or resolution_action on a
 * reconciled SUBMITTED item, whose kind stays SUBMITTED. */
const resolutionOf = (req: AccessRequestNotification): ResolutionAction | null => {
  if (req.kind !== 'SUBMITTED') {
    return req.kind;
  }
  return req.resolution_action ?? null;
};

const accessRequestMessage = (req: AccessRequestNotification): string => {
  const requester = nameAlias(req.requester_name, req.requester_alias);
  const beneficiary = nameAlias(req.beneficiary_name, req.beneficiary_alias);
  const isSelf = req.requester_alias === req.beneficiary_alias;
  const role = req.requested_role;
  const bg = req.business_group_name;
  const resolution = resolutionOf(req);

  // Still pending (a SUBMITTED item with no resolution yet): describe who requested what.
  if (!resolution) {
    return isSelf ? `${requester} requested ${role} for ${bg}` : `${requester} requested ${role} for ${bg} on behalf of ${beneficiary}`;
  }

  // Expired is a system outcome with no human resolver, so it carries no "by ..." suffix.
  if (resolution === 'EXPIRED') {
    return `${beneficiary}'s ${role} request for ${bg} expired after 30 days without review`;
  }

  // "Approved with changes" when the grant differed from the request — in segments OR role.
  const isChanged = resolution === 'APPROVED' && (req.is_partial_approval || req.is_role_changed);
  const verb = isChanged ? 'approved with changes' : { APPROVED: 'approved', REJECTED: 'rejected', CANCELLED: 'cancelled' }[resolution];
  const by = req.resolved_by ? ` by ${req.resolved_by_name ? nameAlias(req.resolved_by_name, req.resolved_by) : req.resolved_by}` : '';
  return `${beneficiary}'s ${role} request for ${bg} was ${verb}${by}`;
};

/** Approved -> tick, rejected/cancelled -> cross, expired -> stopped, pending -> clock. */
const iconForResolution = (resolution: ResolutionAction | null): string => {
  switch (resolution) {
    case 'APPROVED':
      return 'status-positive';
    case 'REJECTED':
    case 'CANCELLED':
      return 'status-negative';
    case 'EXPIRED':
      return 'status-stopped';
    default:
      return 'status-pending';
  }
};

/** Scannable heading; the who/what/where stays in accessRequestMessage. */
const accessRequestTitle = (req: AccessRequestNotification): string => {
  const resolution = resolutionOf(req);
  if (!resolution) {
    // A SUBMITTED item with no resolution is the reviewer's "needs your review" item.
    return 'Access request needs review';
  }
  if (resolution === 'APPROVED') {
    return req.is_partial_approval ? 'Access request approved with changes' : 'Access request approved';
  }
  const titles: Record<Exclude<ResolutionAction, 'APPROVED'>, string> = {
    REJECTED: 'Access request rejected',
    CANCELLED: 'Access request cancelled',
    EXPIRED: 'Access request expired'
  };
  return titles[resolution];
};

/** Semantic counterpart of `iconForResolution`, for UI layers that pick their own icons. */
const severityForResolution = (resolution: ResolutionAction | null): NotificationSeverity => {
  switch (resolution) {
    case 'APPROVED':
      return 'success';
    case 'REJECTED':
    case 'CANCELLED':
    case 'EXPIRED':
      return 'critical';
    default:
      return 'pending';
  }
};

const accessRequestPresenter: NotificationPresenter = {
  owns: (n) => n.source === ACCESS_MANAGEMENT_SOURCE,
  present: (n) => {
    const req = parseAccessRequestNotification(n);
    if (!req) {
      return genericPresentation();
    }
    const resolution = resolutionOf(req);
    // A SUBMITTED item is the reviewer's notification. It goes to the global console, which lists
    // every business group's requests together — these notifications are only sent to Finance
    // Admins, who can view it. The tab follows the request's state: the pending queue while it
    // awaits review, the request history once resolved, since a resolved request has left the
    // queue.
    const route =
      req.kind === 'SUBMITTED'
        ? `${GLOBAL_ACCESS_MANAGEMENT_PATH}?tab=${resolution ? 'request-history' : 'pending-requests'}`
        : // APPROVED/REJECTED/EXPIRED are the requester/beneficiary's own outcome, and their requests page
          // already lists every status.
          '/my-requests';
    return {
      iconName: iconForResolution(resolution),
      severity: severityForResolution(resolution),
      title: accessRequestTitle(req),
      message: accessRequestMessage(req),
      route
    };
  }
};

// ── Registry ─────────────────────────────────────────────────────────────────────────────────────

const PRESENTERS: NotificationPresenter[] = [accessRequestPresenter];

/** Build the presentation for a notification, delegating to the presenter that owns its type. */
export const getNotificationPresentation = (notification: XptNotification): NotificationPresentation => {
  const presenter = PRESENTERS.find((p) => p.owns(notification));
  return presenter ? presenter.present(notification) : genericPresentation();
};
